import { isEmpty } from 'lodash';
import { BriefSection, DynamicSection, SectionTypes } from '@fiverr-private/ai_brief';
import { Gql } from '@fiverr-private/dynamic_matching';
import { useBriefContext } from '@fiverr-private/ai_brief/dist/esm/src/common/BriefContext';
import { BriefInitialValues } from '../../../context/BriefCreationContext';
import { BriefClassificationResponse } from '../../../api';
import { appTranslate } from '../../../i18n';
import {
    BudgetAndTimelineTitleComponent,
    BudgetAndTimelineEditComponentWrapper,
    BudgetAndTimelineViewComponent,
} from './BudgetAndTimelineSection';
import {
    BudgetAndTimelineContent,
    hasOneTimeFixedPriceShape,
    hasOneTimeHourlyShape,
    hasOngoingFixedPriceShape,
    hasOngoingHourlyShape,
    isValidContentStructure,
    TimeUnit,
    HiringDurationType,
    TimeCommitmentType,
    TimeUnitAndValue,
    SECOND_IN_MS,
} from './utils';

export const BUDGET_AND_TIMELINE_SECTION_ID = 'budget_and_timeline';

const mapApiTimeUnit = (apiUnit?: string | null): TimeUnit | undefined => {
    if (!apiUnit) return undefined;
    const unitMap: Record<string, TimeUnit> = {
        DAY: 'days',
        WEEK: 'weeks',
        MONTH: 'months',
    };
    return unitMap[apiUnit.toUpperCase()];
};

const mapApiHiringDuration = (apiDuration?: string | null): HiringDurationType | undefined => {
    if (!apiDuration) return undefined;
    const durationMap: Record<string, HiringDurationType> = {
        MORE_THAN_THREE_MONTHS: 'more_than_three_months',
        ONE_TO_THREE_MONTHS: 'one_to_three_months',
        LESS_THAN_ONE_MONTH: 'less_than_one_month',
    };
    return durationMap[apiDuration.toUpperCase()];
};

const mapApiTimeCommitment = (apiCommitment?: string | null): TimeCommitmentType | undefined => {
    if (!apiCommitment) return undefined;
    const commitmentMap: Record<string, TimeCommitmentType> = {
        OVER_THIRTY_HOURS_PER_WEEK: 'over_thirty_hours_per_week',
        TEN_TO_THIRTY_HOURS_PER_WEEK: 'ten_to_thirty_hours_per_week',
        UP_TO_TEN_HOURS_PER_WEEK: 'up_to_ten_hours_per_week',
    };
    return commitmentMap[apiCommitment.toUpperCase()];
};

const mapApiTimelineDetails = (apiDetails?: { unit?: string; value?: number } | null): TimeUnitAndValue | undefined => {
    if (!apiDetails) return undefined;
    return {
        unit: mapApiTimeUnit(apiDetails.unit),
        value: apiDetails.value,
    };
};

export const getBudgetAndTimelineSection = (
    briefClassification: BriefClassificationResponse,
    briefInitialValues?: Partial<BriefInitialValues>
): DynamicSection => {
    const resolvedData = briefClassification.resolved_data;
    const isHourlyEligible = briefClassification.static_data?.is_hourly_eligible;
    let pricingType: BudgetAndTimelineContent['pricingType'] = null;
    let content: BudgetAndTimelineContent | null = null;

    if (resolvedData) {
        const { budget, delivery_time, need_type } = resolvedData;
        const ongoingWorkPotential = need_type?.ongoingWorkPotential ?? false;

        if (isHourlyEligible || briefInitialValues?.pricingType !== Gql.BriefPricingType.Hourly) {
            pricingType = briefInitialValues?.pricingType ?? null;
        }

        if (!pricingType) {
            switch (budget?.pricing_type?.toLowerCase()) {
                case 'fixed':
                    pricingType = Gql.BriefPricingType.Fixed;
                    break;
                case 'hourly':
                    pricingType = Gql.BriefPricingType.Hourly;
                    break;
                default:
                    pricingType = null;
            }
        }

        // Map timeline
        let timeline;
        switch (delivery_time?.timeline?.toLowerCase()) {
            case 'specific_date':
                timeline = Gql.DmBriefTimeline.SpecificDate;
                break;
            case 'asap':
                timeline = Gql.DmBriefTimeline.SoonAsPossible;
                break;
            case 'flexible':
                timeline = Gql.DmBriefTimeline.Flexible;
                break;
            case 'soon_as_possible':
                timeline = Gql.DmBriefTimeline.SoonAsPossible;
                break;
            default:
                timeline = delivery_time?.timeline;
                break;
        }

        // delivery_date can be either an epoch timestamp string or a "YYYY-MM-DD" date string
        // TODO: Remove this once the API is updated to return only epoch timestamps
        let deliveryDate: number | undefined;
        if (delivery_time?.delivery_date) {
            const asNumber = Number(delivery_time.delivery_date);
            if (!isNaN(asNumber)) {
                // It's a valid epoch timestamp string
                deliveryDate = asNumber * SECOND_IN_MS;
            } else {
                // Try parsing as a date string (YYYY-MM-DD format)
                const parsedDate = new Date(delivery_time.delivery_date);
                if (!isNaN(parsedDate.getTime())) deliveryDate = parsedDate.getTime();
            }
        }

        const timelineDetailsFromApi = delivery_time?.timeline_details;

        content = {
            pricingType,
            budgetInCents: budget?.max_price_in_cents ?? 0,
            hourlyBudgetRange: {
                min: budget?.min_price_in_cents ?? 0,
                max: budget?.max_price_in_cents ?? 0,
            },
            ongoingWorkPotential,
            timeline,
            deliveryDate,
            timelineDetails: {
                generalTimeline: mapApiTimelineDetails(timelineDetailsFromApi?.generalTimeline) ?? {},
                deliveriesFrequency: mapApiTimelineDetails(timelineDetailsFromApi?.deliveriesFrequency) ?? {},
                hiringDuration: mapApiHiringDuration(timelineDetailsFromApi?.hiringDuration),
                timeCommitment: mapApiTimeCommitment(timelineDetailsFromApi?.timeCommitment),
            },
        };
    }

    return {
        id: BUDGET_AND_TIMELINE_SECTION_ID,
        mandatory: true,
        title: appTranslate('sections.budget_and_timeline.title').toString(),
        content,
        included: true,
        type: SectionTypes.dynamic,
        hideActions: true,
        TitleComponent: BudgetAndTimelineTitleComponent,
        ViewComponent: BudgetAndTimelineViewComponent,
        EditComponent: BudgetAndTimelineEditComponentWrapper,
        customValidator: (section: BriefSection) => {
            const sectionContent = section.content as unknown as BudgetAndTimelineContent;

            if (isEmpty(sectionContent)) {
                return false;
            }

            const { budgetInCents, hourlyBudgetRange, pricingType } = sectionContent;
            const { min, max } = hourlyBudgetRange ?? {};

            // Base budget validation
            let isValidBudget = false;
            if (pricingType === Gql.BriefPricingType.Hourly) {
                isValidBudget = !!min && !!max && min > 0 && max > 0 && min < max;
            } else {
                isValidBudget = !!budgetInCents && budgetInCents > 0;
            }

            const validContentStructure =
                hasOneTimeFixedPriceShape(sectionContent) ||
                hasOneTimeHourlyShape(sectionContent) ||
                hasOngoingFixedPriceShape(sectionContent) ||
                hasOngoingHourlyShape(sectionContent);

            return isValidBudget && validContentStructure;
        },
        moveActionsToSide: (section: BriefSection) => {
            const { editedSectionId } = useBriefContext();

            const content: BudgetAndTimelineContent = (section?.content ?? {}) as unknown as BudgetAndTimelineContent;

            return isValidContentStructure(content) || editedSectionId === section.id;
        },
    };
};
